/**
 * Scene3D Module - Vanilla Three.js Integration
 * Creates an immersive 3D floating geometric object with mouse interaction
 */

class Scene3D {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) return;

    // Scene properties
    this.mouse = { x: 0, y: 0 };
    this.targetMouse = { x: 0, y: 0 };
    this.scrollProgress = 0;
    this.isLoaded = false;
    this.time = 0;

    // Performance settings
    this.isMobile = window.innerWidth < 768;
    this.quality = this.isMobile ? 'low' : 'high';
    this.targetFPS = this.isMobile ? 30 : 45;
    this.lastRender = 0;
    this.isPaused = false;

    this.init();
  }

  async init() {
    await this.loadThreeJS();
    this.createScene();
    this.createLights();
    this.createGeometry();
    this.createParticles();
    this.setupEventListeners();
    this.animate();
    
    // Fade in
    setTimeout(() => {
      this.container.style.opacity = '1';
      this.isLoaded = true;
    }, 500);
  }

  loadThreeJS() {
    return new Promise((resolve) => {
      if (window.THREE) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
      script.onload = resolve;
      document.head.appendChild(script);
    });
  }

  createScene() {
    const { THREE } = window;
    
    // Scene
    this.scene = new THREE.Scene();
    
    // Camera
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 100);
    this.camera.position.z = 8;
    
    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: !this.isMobile,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1.25 : 1.5));
    this.renderer.setClearColor(0x000000, 0);
    this.container.appendChild(this.renderer.domElement);
  }

  createLights() {
    const { THREE } = window;
    
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    this.scene.add(ambientLight);
    
    // Main directional lights with blue/purple tones
    const dirLight1 = new THREE.DirectionalLight(0x3b82f6, 0.8);
    dirLight1.position.set(5, 5, 5);
    this.scene.add(dirLight1);
    
    const dirLight2 = new THREE.DirectionalLight(0xa855f7, 0.6);
    dirLight2.position.set(-5, -5, -5);
    this.scene.add(dirLight2);
    
    // Accent point lights
    this.pointLight1 = new THREE.PointLight(0x06b6d4, 0.8, 15);
    this.pointLight1.position.set(3, 3, 3);
    this.scene.add(this.pointLight1);
    
    this.pointLight2 = new THREE.PointLight(0x8b5cf6, 0.6, 12);
    this.pointLight2.position.set(-3, -2, 2);
    this.scene.add(this.pointLight2);
  }

  createGeometry() {
    const { THREE } = window;
    
    // Main icosahedron group
    this.mainGroup = new THREE.Group();
    
    // Outer mesh - Glass-like material
    const outerGeometry = new THREE.IcosahedronGeometry(1.8, this.isMobile ? 2 : 4);
    const outerMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x0a0a0a,
      metalness: 0.9,
      roughness: 0.1,
      transparent: true,
      opacity: 0.7,
      envMapIntensity: 1,
      clearcoat: 1,
      clearcoatRoughness: 0.1,
    });
    this.outerMesh = new THREE.Mesh(outerGeometry, outerMaterial);
    this.mainGroup.add(this.outerMesh);
    
    // Inner wireframe mesh
    const innerGeometry = new THREE.IcosahedronGeometry(1.4, 2);
    const innerMaterial = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      wireframe: true,
      transparent: true,
      opacity: 0.6,
    });
    this.innerMesh = new THREE.Mesh(innerGeometry, innerMaterial);
    this.mainGroup.add(this.innerMesh);
    
    // Core glow sphere
    const coreGeometry = new THREE.SphereGeometry(0.5, 32, 32);
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.8,
    });
    this.coreMesh = new THREE.Mesh(coreGeometry, coreMaterial);
    this.mainGroup.add(this.coreMesh);
    
    // Outer ring
    const ringGeometry = new THREE.TorusGeometry(2.5, 0.02, 16, 100);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x3b82f6,
      transparent: true,
      opacity: 0.3,
    });
    this.ring1 = new THREE.Mesh(ringGeometry, ringMaterial);
    this.ring1.rotation.x = Math.PI / 2;
    this.mainGroup.add(this.ring1);
    
    // Second ring
    this.ring2 = new THREE.Mesh(ringGeometry.clone(), ringMaterial.clone());
    this.ring2.rotation.x = Math.PI / 3;
    this.ring2.rotation.y = Math.PI / 4;
    this.mainGroup.add(this.ring2);
    
    this.scene.add(this.mainGroup);
  }

  createParticles() {
    const { THREE } = window;
    
    const particleCount = this.isMobile ? 100 : 300;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    const color1 = new THREE.Color(0x3b82f6);
    const color2 = new THREE.Color(0xa855f7);
    const color3 = new THREE.Color(0x06b6d4);
    
    for (let i = 0; i < particleCount; i++) {
      // Spherical distribution
      const radius = 4 + Math.random() * 3;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
      
      // Random color from palette
      const colorChoice = Math.random();
      const color = colorChoice < 0.33 ? color1 : colorChoice < 0.66 ? color2 : color3;
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    
    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const particleMaterial = new THREE.PointsMaterial({
      size: 0.03,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: true,
    });
    
    this.particles = new THREE.Points(particleGeometry, particleMaterial);
    this.scene.add(this.particles);
  }

  setupEventListeners() {
    // Mouse movement
    const onMouseMove = (e) => {
      this.targetMouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.targetMouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    const throttledMouseMove = typeof window.rafThrottle === 'function'
      ? window.rafThrottle(onMouseMove)
      : onMouseMove;

    window.addEventListener('mousemove', throttledMouseMove, { passive: true });
    
    // Touch movement for mobile
    window.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) {
        this.targetMouse.x = (e.touches[0].clientX / window.innerWidth) * 2 - 1;
        this.targetMouse.y = -(e.touches[0].clientY / window.innerHeight) * 2 + 1;
      }
    }, { passive: true });
    
    // Scroll progress
    const onScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      this.scrollProgress = scrollTop / docHeight;
    };

    const throttledScroll = typeof window.throttle === 'function'
      ? window.throttle(onScroll, 80)
      : onScroll;

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Resize
    const onResize = () => {
      this.isMobile = window.innerWidth < 768;
      this.targetFPS = this.isMobile ? 30 : 45;
      this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.isMobile ? 1.25 : 1.5));
    };

    const throttledResize = typeof window.throttle === 'function'
      ? window.throttle(onResize, 120)
      : onResize;

    window.addEventListener('resize', throttledResize, { passive: true });

    document.addEventListener('visibilitychange', () => {
      this.isPaused = document.hidden;
    });
  }

  animate(timestamp = 0) {
    requestAnimationFrame(() => this.animate());

    if (this.isPaused) return;

    const frameInterval = 1000 / this.targetFPS;
    if (timestamp - this.lastRender < frameInterval) return;
    this.lastRender = timestamp;
    
    this.time += 0.01;
    
    // Smooth mouse interpolation
    this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.05;
    this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.05;
    
    if (this.mainGroup) {
      // Floating animation
      this.mainGroup.position.y = Math.sin(this.time * 0.5) * 0.3;
      this.mainGroup.position.x = Math.cos(this.time * 0.3) * 0.1;
      
      // Mouse-driven rotation
      const targetRotationX = this.mouse.y * 0.5 + this.time * 0.1;
      const targetRotationY = this.mouse.x * 0.5 + this.time * 0.15;
      
      this.mainGroup.rotation.x += (targetRotationX - this.mainGroup.rotation.x) * 0.05;
      this.mainGroup.rotation.y += (targetRotationY - this.mainGroup.rotation.y) * 0.05;
      
      // Scroll-based scale
      const scale = 1 + this.scrollProgress * 0.3;
      this.mainGroup.scale.setScalar(scale);
      
      // Inner mesh counter-rotation
      if (this.innerMesh) {
        this.innerMesh.rotation.x = -this.mainGroup.rotation.x * 0.3;
        this.innerMesh.rotation.y = -this.mainGroup.rotation.y * 0.3;
      }
      
      // Core pulse
      if (this.coreMesh) {
        const pulse = 1 + Math.sin(this.time * 2) * 0.1;
        this.coreMesh.scale.setScalar(pulse);
        this.coreMesh.material.opacity = 0.5 + Math.sin(this.time * 3) * 0.3;
      }
      
      // Ring animations
      if (this.ring1) {
        this.ring1.rotation.z = this.time * 0.3;
      }
      if (this.ring2) {
        this.ring2.rotation.z = -this.time * 0.2;
        this.ring2.rotation.x = Math.PI / 3 + Math.sin(this.time * 0.5) * 0.2;
      }
    }
    
    // Animate particles
    if (this.particles) {
      this.particles.rotation.y = this.time * 0.05;
      this.particles.rotation.x = Math.sin(this.time * 0.1) * 0.1;
    }
    
    // Animate point lights
    if (this.pointLight1) {
      this.pointLight1.position.x = Math.sin(this.time) * 4;
      this.pointLight1.position.z = Math.cos(this.time) * 4;
    }
    if (this.pointLight2) {
      this.pointLight2.position.x = Math.cos(this.time * 0.7) * 3;
      this.pointLight2.position.y = Math.sin(this.time * 0.7) * 3;
    }
    
    this.renderer.render(this.scene, this.camera);
  }

  // Cleanup method
  destroy() {
    if (this.renderer) {
      this.renderer.dispose();
      this.container.removeChild(this.renderer.domElement);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Wait for page load before initializing 3D scene
  window.addEventListener('load', () => {
    setTimeout(() => {
      window.scene3D = new Scene3D('hero-3d-container');
    }, 1500); // Start after loading animation
  });
});
